<template>
  <h1 v-if="!isNew" class="mb-4 -mt-10 text-2xl font-semibold text-gray-900">
    {{ view ? 'Produit' : 'Modifier produit' }} # {{ id }}
  </h1>

  <component :is="isView ? 'div' : 'form'" @submit.prevent="handleSubmit" class="space-y-8 divide-y divide-gray-200">
    <div class="space-y-6">
      <div class="grid grid-cols-1 gap-y-4 gap-x-4 lg:grid-cols-12">
        <div class="lg:col-span-3">
          <label for="vat" class="block text-sm font-medium text-gray-700"> Code catalogue </label>
          <div class="mt-1 flex rounded-md">
            <input
              v-model="formValues.code"
              type="text"
              name="code"
              id="code"
              :disabled="view"
              class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md"
              :class="{ 'border-gray-200': !isView, 'bg-gray-200 border-gray-200': isView }"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-y-4 gap-x-4 lg:grid-cols-12">
        <div class="lg:col-span-2">
          <label for="vat" class="block text-sm font-medium text-gray-700"> Prix HTVA </label>
          <div class="mt-1 flex rounded-md gap-x-2">
            <input
              v-model="formValues.price"
              type="number"
              step="0.01"
              min="0.01"
              max="999999999.00"
              name="price"
              id="price"
              :disabled="view"
              class="block min-w-64 sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md"
              :class="{ 'border-gray-200': !isView, 'bg-gray-200 border-gray-200': isView }"
            />
            <select
              v-model="formValues.price_type"
              class="block min-w-96 sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md px-2 pr-8"
              :class="{ 'border-gray-200': !isView, 'bg-gray-200 border-gray-200': isView }"
              name="price_type"
              id="price_type"
            >
              <option value="PER_SQUARE_METER">Par M²</option>
              <option value="PER_METER">Par metre</option>
              <option value="FIXED">Prix fixe</option>
            </select>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-y-4 gap-x-4 lg:grid-cols-12">
        <div class="lg:col-span-6">
          <label for="vat" class="block text-sm font-medium text-gray-700"> Lien fournisseur </label>
          <div class="mt-1 flex rounded-md">
            <input
              v-model="formValues.link"
              type="text"
              name="link"
              id="link"
              :disabled="view"
              class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md"
              :class="{ 'border-gray-200': !isView, 'bg-gray-200 border-gray-200': isView }"
            />
          </div>
        </div>
      </div>

      <div class="mt-6 grid grid-cols-1 gap-y-2 gap-x-4 lg:grid-cols-12">
        <div class="lg:col-span-7">
          <label for="street" class="block text-sm font-medium text-gray-700"> Description </label>
          <div class="mt-1 flex rounded-md">
            <textarea
              style="max-height: calc(60vh - 50px)"
              v-model="formValues.description"
              type="text"
              name="street"
              id="street"
              :rows="rows"
              @keypress.enter="rows += 1"
              @keypress.backspace="rows -= 1"
              :disabled="view"
              class="block w-full sm:text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500 border rounded-md"
              :class="{ 'border-gray-200': !isView, 'bg-gray-200 border-gray-200': isView }"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="pt-5">
      <div class="flex">
        <button
          v-if="isNew || isEdit"
          :disabled="disableButtons"
          type="submit"
          class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <i v-if="disableButtons" class="fas fa-spinner mt-1"></i>
          <span v-else>Sauvegarder</span>
        </button>
        <router-link
          v-if="view"
          :disabled="disableButtons"
          :to="`/products/${id}/edit`"
          class="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <i v-if="disableButtons" class="fas fa-spinner mt-1"></i>
          <span v-else>Modifier</span>
        </router-link>
      </div>
    </div>
  </component>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import useApi from '@/hooks/useApi'
import { useRouter } from 'vue-router'

export default {
  props: {
    id: [String, Number],
    view: Boolean,
  },
  setup(props) {
    const router = useRouter()
    const formValues = ref({
      code: '',
      price: '0.00',
      price_type: 'PER_SQUARE_METER',
      link: '',
      description: '',
    })

    const rows = ref(2)

    const isNew = computed(() => !props.view && (!props.id || !(props.id > 0)))
    const isEdit = computed(() => !props.view && !!props.id)
    const isView = computed(() => !!props.view && !!props.id)
    const disableButtons = ref(true)

    watch(
      () => formValues.value.description,
      () => {
        if (formValues.value.description.length > 255) {
          formValues.value.description = formValues.value.description.substr(0, 255)
        }
      },
    )
    watch(
      () => formValues.value.price,
      () => {
        formValues.value.price = String(Math.floor(formValues.value.price * 100) / 100).replace(',', '.')
      },
    )

    onMounted(() => {
      setTimeout(
        () => {
          disableButtons.value = false
        },
        isView.value ? 0 : 1000,
      )
    })

    const api = useApi()
    if (!isNew.value && props.id) {
      api.products.get({ id: props.id }).then(({ data }) => {
        formValues.value = data
      })
    }

    const handleSubmit = () => {
      if (isView.value) return
      if (isNew.value) {
        api.products.create(formValues.value).then(() => {
          router.push('/products')
        })
        return
      }
      if (isEdit.value) {
        api.products.edit(formValues.value).then(() => {
          router.push('/products')
        })
        return
      }
    }

    return { isNew, isEdit, isView, disableButtons, formValues, rows, handleSubmit }
  },
}
</script>

<style></style>
